import { Assets, type Spritesheet, type Texture } from "pixi.js";

/**
 * Shared atlases: D-121 ancient artwork over the established Kenney terrain.
 *
 * Loaded once and shared by every board. If it fails — offline on first run,
 * or in a test without a network — the board falls back to the flat shapes it
 * drew before, so a missing picture never costs anyone a game.
 */

/**
 * The atlas is drawn at 2x and says so (`"scale": "2"` in board.json), so
 * Pixi already treats its pixels as half-size. Sprites are shown at 1.
 */
export const ART_SCALE = 1;

let loading: Promise<Spritesheet | null> | null = null;

export function loadBoardArt(): Promise<Spritesheet | null> {
  loading ??= loadArtWithFallback("board", "ancient-board").then((sheet) =>
    withDetails(sheet, "ancient-mechanisms").then((base) =>
      withDetails(base, "ancient-tower-actions")
        .then((towers) => withDetails(towers, "ancient-buildings"))
        .then((complete) => {
          if (complete?.textures.main_building)
            complete.textures.core = complete.textures.main_building;
          return complete;
        })
    )
  );
  return loading;
}

let loadingUnits: Promise<Spritesheet | null> | null = null;

/**
 * Authored walk poses in four facings, packed by tools/art/ancient/build.py.
 * The earlier CC0 unit atlas and then the geometric marks remain fallbacks.
 */
export function loadUnitArt(): Promise<Spritesheet | null> {
  loadingUnits ??= Assets.load<Spritesheet>(`${import.meta.env.BASE_URL}art/rigged-units.json`)
    .catch(() => null)
    .then(
      (rigged) =>
        rigged ??
        loadArtWithFallback("units", "ancient-units").then((sheet) =>
          withDetails(sheet, "ancient-unit-states").then((base) =>
            withDetails(base, "ancient-actions")
          )
        )
    );
  return loadingUnits;
}

export async function withDetails(
  sheet: Spritesheet | null,
  name: string
): Promise<Spritesheet | null> {
  const details = await Assets.load<Spritesheet>(
    `${import.meta.env.BASE_URL}art/${name}.json`
  ).catch(() => null);
  if (sheet && details) Object.assign(sheet.textures, details.textures);
  return sheet ?? details;
}

/** Keep the established terrain/decor and replace only approved pictures. Missing art is harmless. */
export async function loadArtWithFallback(
  legacy: string,
  ancient: string
): Promise<Spritesheet | null> {
  const load = (name: string) =>
    Assets.load<Spritesheet>(`${import.meta.env.BASE_URL}art/${name}.json`).catch(() => null);
  const [base, upgrade] = await Promise.all([load(legacy), load(ancient)]);
  if (base && upgrade) Object.assign(base.textures, upgrade.textures);
  return base ?? upgrade;
}

export function frame(sheet: Spritesheet, name: string): Texture | null {
  return sheet.textures[name] ?? null;
}
